import React from "react"

interface UseModalBackgroundCloseProps {
  excludeIds?: string[]
  excludeRefs?: Array<React.RefObject<HTMLElement>>
  isOpen: boolean
  modalRef: React.RefObject<HTMLElement>
  onRequestClose: (...args: any[]) => void
}

const useModalBackgroundClose = ({
  excludeIds = [],
  excludeRefs = [],
  isOpen,
  modalRef,
  onRequestClose,
}: UseModalBackgroundCloseProps) => {
  React.useEffect(() => {
    if (!modalRef?.current || !isOpen) return

    const body = document.body
    const classList = modalRef.current.classList

    // Ensure classList exists and has at least one class
    if (!classList || classList.length === 0) return

    const refClass = classList[0]
    const refLabel = `modalClassRef-${refClass}`

    body.classList.add("noScroll", refLabel)

    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node

      // Check if click is outside body
      if (!body.contains(target)) return

      // Check excluded refs
      if (excludeRefs.some((ref) => ref.current?.contains(target))) return

      // Check excluded IDs
      if (
        excludeIds.some((id) => document.getElementById(id)?.contains(target))
      )
        return

      // Check if click is outside modal
      if (!modalRef.current?.contains(target)) {
        onRequestClose()
      }
    }

    document.addEventListener("click", handleClick)

    return () => {
      document.removeEventListener("click", handleClick)
      body.classList.remove(refLabel)

      // Only remove noScroll if no other modals are open
      const hasOpenModals = Array.from(body.classList).some((cls) =>
        cls.startsWith("modalClassRef-")
      )
      if (!hasOpenModals) {
        body.classList.remove("noScroll")
      }
    }
  }, [isOpen, modalRef, excludeRefs, excludeIds, onRequestClose])
}

export default useModalBackgroundClose
